/**
 * Gifts Progress Web Component
 * 
 * A comprehensive Shopify cart enhancement component that provides:
 * 
 * 🎯 **Core Features:**
 * - Dynamic progress bar showing cart value progression
 * - Multiple milestone types (free shipping, free gifts)
 * - Automatic gift addition/removal based on cart thresholds
 * - Real-time cart synchronization and state management
 * 
 * 🛒 **Cart Integration:**
 * - Listens to Shopify cart events (does not block theme cart:change — that broke the drawer)
 * - Debounced / throttled cart.js access (avoids 429s and slow cart UX)
 * - Fresh cart data validation for accurate state
 * 
 * 🎁 **Gift Management:**
 * - Auto-adds eligible gifts when thresholds are met
 * - Removes ineligible gifts when cart value drops
 * - Tracks gift quantities (1 free + unlimited paid)
 * - Cleans up orphaned gifts from cart
 * 
 * 🔧 **Technical Features:**
 * - Event-driven architecture with custom event handling
 * - Debounced cart operations to prevent race conditions
 * - Comprehensive error handling and logging
 * - Debug mode for development (set debug = true)
 * - Initialization guards to prevent multiple setups
 * 
 * 📊 **Progress Visualization:**
 * - Visual progress bar with milestone markers
 * - Dynamic messaging based on cart status
 * - Responsive milestone positioning
 * - Achievement state management
 * 
 * 🚀 **Usage:**
 * ```html
 * <gifts-progress>
 *   <!-- Liquid-rendered milestone blocks -->
 * </gifts-progress>
 * ```
 * 
 * 🐛 **Debug Mode:**
 * ```javascript
 * document.querySelector('gifts-progress').debug = true;
 * ```
 * 
 * @version 2.0.0
 * @author Enchant Digital
 * @requires Shopify Liquid templates for milestone configuration
 */

// Prevent redeclaration if script is loaded multiple times
if (!window.customElements.get('gifts-progress')) {

/**
 * Serialize all gifts-progress cart AJAX (cart.js, cart/add.js, cart/change.js) so we
 * never overlap theme / app traffic — preview + gift logic was causing 429s and HTML
 * rate-limit pages (breaks `.json()` in theme.js).
 */
let __gpCartTail = Promise.resolve();
let __gpCartLastEndAt = 0;
/** Space between our cart AJAX calls; lower = snappier but raises 429 risk with heavy apps */
const __GP_CART_API_GAP_MS = 280;

function __gpRunCartApi(task) {
  const prev = __gpCartTail;
  let release;
  __gpCartTail = new Promise((r) => {
    release = r;
  });
  const scheduled = prev.catch(() => {}).then(async () => {
    const wait = __GP_CART_API_GAP_MS - (Date.now() - __gpCartLastEndAt);
    if (wait > 0) {
      await new Promise((r) => setTimeout(r, wait));
    }
    try {
      return await task();
    } finally {
      __gpCartLastEndAt = Date.now();
      release();
    }
  });
  return scheduled;
}

class GiftsProgress extends HTMLElement {
  constructor() {
    super();
    this.milestones = [];
    this.cartTotal = 0;
    this.addedGifts = new Map(); // Changed to Map to track gift details
    this.giftLineItems = new Map(); // Track line item IDs for gifts
    this.abortController = new AbortController();
    this.isProcessingCartChange = false; // Flag to prevent recursive cart events
    this.pendingCartUpdates = new Set(); // Track pending cart updates
    this.cartEventDebounceTimer = null; // Debounce timer for cart events
    /** Debounce rapid theme cart:change / line-item:change bursts into one run */
    this._handleCartDebounceTimer = null;
    /** Single in-flight cart.js request (coalesced); spacing vs mutations via __gpRunCartApi */
    this._cartFetchInFlight = null;
    /** Only delays auto-gift work; progress bar updates immediately on cart events */
    this.HANDLE_CART_DEBOUNCE_MS = 200;
    this.lastCartTotal = 0; // Track last cart total to avoid unnecessary updates
    this.lastItemCount = 0; // Line count — must change when €0 gifts change (total can stay same)
    /** Gift keys the customer removed while still above threshold — do not auto-readd until threshold is not met again */
    this.userDeclinedGifts = new Set();
    /** Snapshot of `_gift_key` values from last cart observation (for detecting manual removal) */
    this._previousGiftKeysFromCart = null;
    /** Latest cart from theme events coalesced into debounced checkForAutoGifts */
    this._debounceCartSnapshot = null;
    /** Persisted across drawer DOM remounts (section re-render disconnects this element) */
    this._declinedStorageKey = 'gifts-progress:declined-gift-keys';
    this.isInitialized = false; // Prevent multiple initializations
    this.initializationPromise = null; // Track initialization promise
    this.lastInitializationTime = 0; // Track last initialization time
    this.debug = false; // Debug flag - set to true to enable console logging
    this.hasAutoAddGiftMilestones = false;
    this.cartItems = [];
    this.freeShipProductIds = new Set();
    this.freeShipVariantIds = new Set();
  }

  // Helper method for debug logging
  debugLog(...args) {
    if (this.debug) {
      console.log(...args);
    }
  }

  debugWarn(...args) {
    if (this.debug) {
      console.warn(...args);
    }
  }

  debugError(...args) {
    if (this.debug) {
      console.error(...args);
    }
  }

  async connectedCallback() {
    // Prevent multiple initializations
    if (this.isInitialized) {
      this.debugLog('[GIFTS-PROGRESS] Already initialized, skipping...');
      return;
    }
    
    // Debounce rapid re-initializations
    const now = Date.now();
    if (now - this.lastInitializationTime < 1000) {
      this.debugLog('[GIFTS-PROGRESS] Too soon since last initialization, skipping...');
      return;
    }
    this.lastInitializationTime = now;
    
    // If initialization is in progress, wait for it
    if (this.initializationPromise) {
      this.debugLog('[GIFTS-PROGRESS] Initialization in progress, waiting...');
      await this.initializationPromise;
      return;
    }
    
    // Start initialization
    this.initializationPromise = this.initialize();
    await this.initializationPromise;
  }

  async initialize() {
    try {
      this.debugLog('[GIFTS-PROGRESS] Initializing...');
      
      // Get milestones from DOM (rendered by Liquid)
      this.scanMilestones();
      this._restoreUserDeclinedGifts();
      this.parseFreeShipProductIds();
      this.parseFreeShipVariantIds();

      // Get initial cart snapshot (Shopify.cart may exist on first paint)
      this.cartTotal = window.Shopify?.cart?.total_price || 0;
      this.lastItemCount = window.Shopify?.cart?.item_count ?? 0;
      this.cartItems = window.Shopify?.cart?.items || [];
      
      this.setupEventListeners();
      this.updateProgress();
      // Was gated on full async init — that blocked the bar for 2–3s (cart queue + validation).
      this.classList.add('gifts-progress--initialized');

      const syncedCart = await this.syncWithCart();
      this.updateProgress();
      this.isInitialized = true;

      if (this.hasAutoAddGiftMilestones) {
        await this.checkForAutoGifts(syncedCart);
        await this.cleanupIneligibleGifts();
      }
      this.updateProgress();

      this.debugLog('[GIFTS-PROGRESS] Initialized with', this.milestones.length, 'milestones');
    } catch (error) {
      this.debugError('[GIFTS-PROGRESS] Failed to initialize', error);
      this.classList.add('gifts-progress--initialized');
      this.isInitialized = true;
    } finally {
      this.initializationPromise = null;
    }
  }

  disconnectedCallback() {
    this.abortController.abort();

    if (this.cartEventDebounceTimer) {
      clearTimeout(this.cartEventDebounceTimer);
      this.cartEventDebounceTimer = null;
    }
    if (this._handleCartDebounceTimer) {
      clearTimeout(this._handleCartDebounceTimer);
      this._handleCartDebounceTimer = null;
    }
  }

  /**
   * Coalesced GET cart.js + global queue + 429 backoff (response may be HTML on rate limit).
   */
  async _loadCartFromServer() {
    if (this._cartFetchInFlight) {
      return this._cartFetchInFlight;
    }

    const run = async () => {
      return __gpRunCartApi(async () => {
        const url = `${Shopify.routes.root}cart.js`;
        let res;
        for (let attempt = 0; attempt < 6; attempt++) {
          res = await fetch(url, { headers: { Accept: 'application/json' } });
          if (res.status === 429) {
            const backoff = Math.min(1200 * Math.pow(2, attempt), 15000);
            this.debugWarn('[GIFTS-PROGRESS] cart.js 429 — backing off ms:', backoff);
            await new Promise((r) => setTimeout(r, backoff));
            continue;
          }
          break;
        }
        const text = await res.text();
        if (!res.ok) {
          throw new Error(`[GIFTS-PROGRESS] cart.js ${res.status}: ${text.slice(0, 200)}`);
        }
        try {
          return JSON.parse(text);
        } catch (e) {
          throw new Error(
            `[GIFTS-PROGRESS] cart.js not JSON (rate limit page?): ${text.slice(0, 160)}`
          );
        }
      });
    };

    this._cartFetchInFlight = run().finally(() => {
      this._cartFetchInFlight = null;
    });
    return this._cartFetchInFlight;
  }

  /** POST cart/add.js or cart/change.js through the same throttle + 429 retries */
  async _postCartJson(relPath, body) {
    return __gpRunCartApi(async () => {
      const url = `${Shopify.routes.root}${relPath}`;
      for (let attempt = 0; attempt < 6; attempt++) {
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        });
        if (res.status === 429) {
          const backoff = Math.min(1200 * Math.pow(2, attempt), 15000);
          this.debugWarn('[GIFTS-PROGRESS]', relPath, '429 — backing off ms:', backoff);
          await new Promise((r) => setTimeout(r, backoff));
          continue;
        }
        return res;
      }
      throw new Error(`[GIFTS-PROGRESS] ${relPath} 429 retries exhausted`);
    });
  }

  /**
   * Notify theme once: optional cart avoids an extra cart.js round-trip.
   * (Previously this fetched cart.js again and dispatched cart:change twice.)
   */
  async notifyThemeCartUpdated(cart, baseEvent = 'gifts-progress:refresh') {
    try {
      let payload = cart;
      if (!payload) {
        payload = await this._loadCartFromServer();
      }
      this.cartTotal = payload.total_price;
      this.lastCartTotal = payload.total_price;
      this.lastItemCount = payload.item_count;

      this.isManualRefresh = true;
      document.dispatchEvent(
        new CustomEvent('cart:change', {
          detail: { cart: payload, baseEvent, fromGiftsProgress: true }
        })
      );
      // Do not dispatch cart:refresh here: CartDrawer already refreshes on cart:change,
      // and a second cart:refresh doubled section fetches and remounted this component.
      setTimeout(() => {
        this.isManualRefresh = false;
      }, 120);
      this.debugLog('[GIFTS-PROGRESS] Theme cart notify done');
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] notifyThemeCartUpdated failed:', error);
    }
  }

  /** @deprecated Use notifyThemeCartUpdated(cart) */
  async forceCartDrawerRefresh() {
    await this.notifyThemeCartUpdated(null, 'gifts-progress:refresh');
  }

  scanMilestones() {
    this.milestones = [];
    
    // Scan individual blocks from content_for 'blocks'
    const shippingBlocks = this.querySelectorAll('.free-shipping-block');
    const giftBlocks = this.querySelectorAll('.free-gift-block');
    
    [...shippingBlocks, ...giftBlocks].forEach(block => {
      // Thresholds set in the app are in the shop's currency; the cart is in the buyer's.
      const rate = block.dataset.shopCurrency === 'true' ? Number(window.Shopify?.currency?.rate) || 1 : 1;
      const threshold = Math.round((parseInt(block.dataset.threshold) || 0) * rate);
      if (rate !== 1) {
        const thresholdText = block.querySelector('.free-shipping-block__threshold, .free-gift-block__threshold');
        if (thresholdText) thresholdText.textContent = this.formatMoney(threshold);
      }

      const milestone = {
        element: block,
        threshold,
        type: block.classList.contains('free-shipping-block') ? 'shipping' : 'gift',
        variantId: block.dataset.variantId,
        autoAdd:
          block.classList.contains('free-gift-block') &&
          block.dataset.autoAdd !== 'false',
        // Track if the gift should be hidden from the progress bar
        hidden: block.dataset.hidden === 'true',
        // Track if the gift requires subscription
        triggerMode: block.dataset.triggerMode || 'any'
      };
      
      if (milestone.threshold > 0) {
        // For gift milestones, validate that we have a variant ID
        if (milestone.type === 'gift' && (!milestone.variantId || milestone.variantId === '')) {
          this.debugWarn('[GIFTS-PROGRESS] Gift milestone missing variant ID:', milestone);
          return;
        }
        
        this.milestones.push(milestone);
        this.debugLog('[GIFTS-PROGRESS] Found milestone:', milestone);
      }
    });
    
    // Sort by threshold
    this.milestones.sort((a, b) => a.threshold - b.threshold);

    this.hasAutoAddGiftMilestones = this.milestones.some(
      (m) => m.type === 'gift' && m.autoAdd && m.variantId
    );
    
    this.debugLog('[GIFTS-PROGRESS] Scanned milestones:', this.milestones);
  }

  /**
   * Compare free-gift keys vs last snapshot; if a gift line vanished while the offer is still valid,
   * treat as manual removal and block auto-readd until cart drops below that milestone threshold.
   */
  _updateGiftKeySnapshot(cart, detectRemoval) {
    const items = cart.items || [];
    const currentKeys = new Set(
      items
        .filter(
          (i) =>
            i.properties &&
            String(i.properties._gift) === 'true' &&
            i.properties._gift_key
        )
        .map((i) => String(i.properties._gift_key))
    );

    if (
      detectRemoval &&
      this._previousGiftKeysFromCart instanceof Set &&
      this._previousGiftKeysFromCart.size > 0
    ) {
      for (const prevKey of this._previousGiftKeysFromCart) {
        if (currentKeys.has(prevKey)) continue;

        const milestone = this.milestones.find(
          (m) =>
            m.type === 'gift' &&
            m.autoAdd &&
            `${m.variantId}_${m.threshold}` === prevKey
        );
        if (!milestone) continue;

        const hasSubscription = this.hasSubscriptionInCart(items);
        const meetsThreshold = cart.total_price >= milestone.threshold;
        const meetsSub =
          milestone.triggerMode === 'any' ||
          (milestone.triggerMode === 'subscription' && hasSubscription);

        if (meetsThreshold && meetsSub) {
          this.userDeclinedGifts.add(prevKey);
          this.addedGifts.delete(prevKey);
          this.giftLineItems.delete(prevKey);
          this.debugLog(
            '[GIFTS-PROGRESS] Free gift removed while offer still valid; auto-readd off until cart drops below threshold',
            prevKey
          );
        }
      }
    }

    this._previousGiftKeysFromCart = currentKeys;
    this._persistUserDeclinedGifts();
  }

  _restoreUserDeclinedGifts() {
    try {
      const raw = sessionStorage.getItem(this._declinedStorageKey);
      if (!raw) {
        return;
      }
      const keys = JSON.parse(raw);
      if (!Array.isArray(keys)) {
        return;
      }
      keys.forEach((k) => this.userDeclinedGifts.add(String(k)));
      this.debugLog('[GIFTS-PROGRESS] Restored declined gifts from session', keys.length);
    } catch (e) {
      /* ignore */
    }
  }

  _persistUserDeclinedGifts() {
    try {
      if (this.userDeclinedGifts.size === 0) {
        sessionStorage.removeItem(this._declinedStorageKey);
      } else {
        sessionStorage.setItem(
          this._declinedStorageKey,
          JSON.stringify([...this.userDeclinedGifts])
        );
      }
    } catch (e) {
      /* ignore */
    }
  }

  setupEventListeners() {
    this.debugLog('[GIFTS-PROGRESS] Setting up event listeners...');
    
    // Single consolidated cart change handler to reduce redundancy
    const handleCartChange = (event) => {
      if (!this.isInitialized) {
        this.debugLog('[GIFTS-PROGRESS] Skipping cart change (not initialized yet)');
        return;
      }
      if (this.isManualRefresh) {
        this.debugLog('[GIFTS-PROGRESS] Skipping cart change (manual refresh in progress)');
        return;
      }
      if (this.isProcessingCartChange) {
        this.debugLog('[GIFTS-PROGRESS] Ignoring cart change event (already processing)');
        return;
      }

      const cart = event.detail?.cart;
      if (!cart) {
        return;
      }

      if (event.detail?.fromGiftsProgress || event.detail?.fromSkioV2SubscriptionGifts) {
        return;
      }

      // Run immediately: drawer re-render disconnects this node and clears the debounce
      // timer, which previously skipped decline detection and allowed gifts to reappear.
      this._updateGiftKeySnapshot(cart, true);

      this.cartItems = cart.items || [];
      this.cartTotal = cart.total_price;
      this.lastItemCount = cart.item_count;
      this.updateProgress();

      this._debounceCartSnapshot = cart;
      if (this._handleCartDebounceTimer) {
        clearTimeout(this._handleCartDebounceTimer);
      }
      this._handleCartDebounceTimer = setTimeout(() => {
        this._handleCartDebounceTimer = null;

        const latest = this._debounceCartSnapshot;
        this._debounceCartSnapshot = null;
        if (!latest) {
          return;
        }

        if (!this.hasAutoAddGiftMilestones) {
          return;
        }

        this.debugLog('[GIFTS-PROGRESS] Auto-gift check (debounced)');
        this.checkForAutoGifts(latest);
      }, this.HANDLE_CART_DEBOUNCE_MS);
    };
    
    // Listen to cart changes - prevent recursive processing
    document.addEventListener('cart:change', handleCartChange, { signal: this.abortController.signal });
    document.addEventListener('variant:add', handleCartChange, { signal: this.abortController.signal });
    document.addEventListener('line-item:change', handleCartChange, { signal: this.abortController.signal });
  }

  async updateFromCart() {
    try {
      const cart = await this._loadCartFromServer();
      if (this.isInitialized) {
        this._updateGiftKeySnapshot(cart, true);
      }
      this.cartTotal = cart.total_price;
      this.lastItemCount = cart.item_count;
      this.cartItems = cart.items || [];
      this.updateProgress();
      this.checkForAutoGifts(cart);
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to fetch cart for gifts progress:', error);
    }
  }

  hasSubscriptionInCart(cartItems) {
    for (const item of cartItems) {
      if (this.itemHasSubscription(item)) {
        return true;
      }
    }
    return false;
  }

  /** Returns true when a cart line is on a Shopify or custom subscription plan. */
  itemHasSubscription(item) {
    if (!item) return false;

    if (item.selling_plan_allocation?.selling_plan) {
      return true;
    }

    const properties = item.properties;
    if (!properties) return false;

    return Boolean(
      properties.subscription === 'true' ||
      properties.subscription_id ||
      properties.frequency_num ||
      properties.frequency_type ||
      properties.frequency_type_text
    );
  }

  /** Parses free-ship product IDs passed from Liquid into a Set. */
  parseFreeShipProductIds() {
    const raw = this.dataset.freeShipProductIds || '';
    this.freeShipProductIds = new Set(
      raw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
  }

  /** Parses free-ship variant IDs passed from Liquid into a Set. */
  parseFreeShipVariantIds() {
    const raw = this.dataset.freeShipVariantIds || '';
    this.freeShipVariantIds = new Set(
      raw
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );
  }

  /** Returns true when a variant flagged custom.free_shipping is in the cart, on any purchase type. */
  hasFreeShipVariantUnlock(cartItems = this.cartItems) {
    const items = cartItems || [];

    if (items.length && this.freeShipVariantIds.size) {
      return items.some((item) => this.freeShipVariantIds.has(String(item.variant_id)));
    }

    return this.dataset.freeShipVariantUnlock === 'true';
  }

  /** Any rule that unlocks the shipping milestone regardless of cart value. */
  hasFreeShipOverride(cartItems = this.cartItems) {
    if (this.dataset.freeShipApp === 'true') {
      return this.hasAppFreeShipUnlock(cartItems || []);
    }

    return (
      this.hasFreeShipSubscriptionUnlock(cartItems) ||
      this.hasFreeShipVariantUnlock(cartItems)
    );
  }

  /**
   * The app's shipping rules besides the threshold: any subscription in the cart, or a cart
   * whose paid items are all free-shipping exception products.
   */
  hasAppFreeShipUnlock(cartItems) {
    if (cartItems.some((item) => this.itemHasSubscription(item))) return true;

    const exceptionIds = (this.dataset.freeShipExceptionProductIds || '').split(',').filter(Boolean);
    const paidItems = cartItems.filter((item) => item.final_line_price > 0);
    return (
      exceptionIds.length > 0 &&
      paidItems.length > 0 &&
      paidItems.every((item) => exceptionIds.includes(String(item.product_id)))
    );
  }

  /** Returns true when a free-ship tagged product is on a subscription. */
  hasFreeShipSubscriptionUnlock(cartItems = this.cartItems) {
    const items = cartItems || [];

    if (items.length && this.freeShipProductIds.size) {
      return items.some(
        (item) =>
          this.freeShipProductIds.has(String(item.product_id)) &&
          this.itemHasSubscription(item)
      );
    }

    return this.dataset.freeShipSubscriptionUnlock === 'true';
  }

  async checkForAutoGifts(cart = null) {
    if (!this.hasAutoAddGiftMilestones) {
      return;
    }

    if (!cart) {
      this.fetchCartAndCheckGifts();
      return;
    }

    // Prevent recursive processing
    if (this.isProcessingCartChange) {
      this.debugLog('[GIFTS-PROGRESS] Already processing cart change, skipping checkForAutoGifts');
      return;
    }

    this.isProcessingCartChange = true;
    this.pendingCartUpdates.clear();

    try {
      const cartItems = cart.items || [];
      this.cartItems = cartItems;
      this._cartAttributes = cart.attributes || {};
      const hasSubscription = this.hasSubscriptionInCart(cartItems);
      const cartValue =
        typeof cart.total_price === 'number' ? cart.total_price : this.cartTotal;
      this.cartTotal = cartValue;
      if (typeof cart.item_count === 'number') {
        this.lastItemCount = cart.item_count;
      }

      this.debugLog(
        '[GIFTS-PROGRESS] Processing cart with',
        cartItems.length,
        'items, total:',
        cartValue,
        'hasSubscription:',
        hasSubscription
      );

      // Check each gift milestone
      for (const milestone of this.milestones) {
        if (milestone.type === 'gift' && milestone.autoAdd && milestone.variantId) {
          const meetsThreshold = cartValue >= milestone.threshold;
          const meetsSubscriptionRequirement = milestone.triggerMode === 'any' || 
            (milestone.triggerMode === 'subscription' && hasSubscription);
          
          const isEligible = meetsThreshold && meetsSubscriptionRequirement;
          const giftKey = `${milestone.variantId}_${milestone.threshold}`;
          
          // Find existing FREE gift items for this milestone (with gift properties)
          const existingFreeGiftItems = cartItems.filter(item => 
            item.variant_id === parseInt(milestone.variantId) && 
            item.properties && 
            item.properties._gift === 'true' &&
            item.properties._gift_key === giftKey
          );
          
          // Find existing PAID gift items (same variant but without gift properties)
          const existingPaidGiftItems = cartItems.filter(item => 
            item.variant_id === parseInt(milestone.variantId) && 
            (!item.properties || item.properties._gift !== 'true')
          );
          
          const hasFreeGift = existingFreeGiftItems.length > 0;
          const totalFreeGiftQuantity = existingFreeGiftItems.reduce((sum, item) => sum + item.quantity, 0);
          const totalPaidGiftQuantity = existingPaidGiftItems.reduce((sum, item) => sum + item.quantity, 0);

          this.debugLog(`[GIFTS-PROGRESS] Checking gift milestone: ${giftKey}, eligible: ${isEligible}, hasFreeGift: ${hasFreeGift}, freeQty: ${totalFreeGiftQuantity}, paidQty: ${totalPaidGiftQuantity}, tracked: ${this.addedGifts.has(giftKey)}`);

          if (!isEligible && this.userDeclinedGifts.has(giftKey)) {
            this.userDeclinedGifts.delete(giftKey);
            this._persistUserDeclinedGifts();
            this.debugLog('[GIFTS-PROGRESS] Cart below threshold; gift can auto-apply again when qualified', giftKey);
          }

          if (isEligible && !hasFreeGift && !this.addedGifts.has(giftKey) && !this.userDeclinedGifts.has(giftKey)) {
            // Add free gift when threshold is met and no free gift exists
            this.debugLog('[GIFTS-PROGRESS] Adding free gift:', giftKey);
            this.pendingCartUpdates.add('add');
            await this.addGiftToCart(milestone, giftKey);
          } else if (isEligible && hasFreeGift && totalFreeGiftQuantity > 1) {
            // If user manually increased quantity of free gift, reduce it back to 1
            // This ensures only 1 free gift, but allows paid quantities
            this.debugLog('[GIFTS-PROGRESS] Reducing free gift quantity to 1:', giftKey);
            this.pendingCartUpdates.add('adjust');
            await this.adjustGiftQuantity(milestone, giftKey, cartItems, 1);
          } else if (!isEligible && hasFreeGift && this.addedGifts.has(giftKey)) {
            // Remove free gift when threshold is no longer met
            // But keep any paid quantities of the same product
            this.debugLog('[GIFTS-PROGRESS] Removing free gift:', giftKey);
            this.pendingCartUpdates.add('remove');
            await this.removeGiftFromCart(milestone, giftKey, cartItems);
          }
        }
      }

    } finally {
      this.isProcessingCartChange = false;
      
      // Only dispatch a single cart change event if we made any updates
      // and batch all updates together
      if (this.pendingCartUpdates.size > 0) {
        try {
          await new Promise((resolve) => setTimeout(resolve, 50));
          const cart = await this._loadCartFromServer();
          const baseEvent = this.pendingCartUpdates.has('add')
            ? 'variant:add'
            : 'line-item:change';
          this.dispatchCartChangeEvent(cart, baseEvent);
        } catch (error) {
          this.debugWarn('[GIFTS-PROGRESS] Failed to dispatch final cart change event:', error);
        }
      }
    }
  }

  async fetchCartAndCheckGifts() {
    try {
      const cart = await this._loadCartFromServer();
      if (this.isInitialized) {
        this._updateGiftKeySnapshot(cart, true);
      }
      this.cartTotal = cart.total_price;
      this.lastItemCount = cart.item_count;
      this.cartItems = cart.items || [];
      this.updateProgress();
      this.checkForAutoGifts(cart);
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to fetch cart for gifts progress:', error);
    }
  }

  async syncWithCart() {
    try {
      const cart = await this._loadCartFromServer();
      this.cartTotal = cart.total_price;
      this.lastItemCount = cart.item_count;
      this.cartItems = cart.items || [];

      // Clear existing tracking
      this.addedGifts.clear();
      this.giftLineItems.clear();
      
      // Sync with existing cart items
      cart.items.forEach(item => {
        if (item.properties && item.properties._gift === 'true' && item.properties._gift_key) {
          const giftKey = item.properties._gift_key;
          const threshold = parseInt(item.properties._gift_threshold);
          
          // Only track gifts that are still eligible (above threshold)
          const isEligible = this.cartTotal >= threshold;
          
          if (isEligible) {
            this.addedGifts.set(giftKey, {
              variantId: item.variant_id.toString(),
              threshold: threshold,
              lineItemKey: item.key
            });
            this.giftLineItems.set(giftKey, item.key);
            this.debugLog('[GIFTS-PROGRESS] Tracking eligible gift:', giftKey);
          } else {
            this.debugLog('[GIFTS-PROGRESS] Gift no longer eligible, will be removed:', giftKey);
          }
        }
      });
      
      // Clean up any orphaned gifts (gifts in cart but not in our milestones)
      await this.cleanupOrphanedGifts(cart);

      this._updateGiftKeySnapshot(cart, false);
      
      this.debugLog('[GIFTS-PROGRESS] Synced with cart:', this.addedGifts.size, 'eligible gifts found');
      return cart;
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to sync with cart:', error);
      return null;
    }
  }

  async cleanupIneligibleGifts() {
    try {
      this.debugLog('[GIFTS-PROGRESS] Cleaning up ineligible gifts...');
      
      let cart = await this._loadCartFromServer();
      
      // Find all gift items that are no longer eligible
      const cartTotal = cart.total_price;
      const ineligibleGifts = cart.items.filter((item) => {
        if (!item.properties || item.properties._gift !== 'true') {
          return false;
        }

        const threshold = parseInt(item.properties._gift_threshold, 10);
        return cartTotal < threshold;
      });
      
      if (ineligibleGifts.length > 0) {
        this.debugLog('[GIFTS-PROGRESS] Found', ineligibleGifts.length, 'ineligible gifts to remove');
        
        for (const gift of ineligibleGifts) {
          this.debugLog('[GIFTS-PROGRESS] Removing ineligible gift:', gift.key);
          
          const response = await this._postCartJson('cart/change.js', {
            id: gift.key,
            quantity: 0,
          });

          if (response.ok) {
            this.debugLog('[GIFTS-PROGRESS] ✅ Removed ineligible gift');
          } else {
            this.debugWarn('[GIFTS-PROGRESS] Failed to remove ineligible gift:', await response.text());
          }
        }

        cart = await this._loadCartFromServer();
        await this.notifyThemeCartUpdated(cart, 'line-item:change');
      }
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to cleanup ineligible gifts:', error);
    }
  }

  async cleanupOrphanedGifts(cart) {
    const giftMilestoneVariantIds = new Set(
      this.milestones
        .filter(m => m.type === 'gift' && m.variantId)
        .map(m => parseInt(m.variantId))
    );

    const orphanedGifts = cart.items.filter(item => 
      item.properties && 
      item.properties._gift === 'true' && 
      !giftMilestoneVariantIds.has(item.variant_id)
    );

    for (const orphanedGift of orphanedGifts) {
      this.debugLog('[GIFTS-PROGRESS] Removing orphaned gift:', orphanedGift.product_id);
      try {
        const response = await this._postCartJson('cart/change.js', {
          id: orphanedGift.key,
          quantity: 0,
        });

        if (response.ok) {
          this.debugLog('[GIFTS-PROGRESS] Orphaned gift removed successfully');
        }
      } catch (error) {
        this.debugWarn('[GIFTS-PROGRESS] Failed to remove orphaned gift:', error);
      }
    }
  }

  async validateGiftProducts() {
    if (!/[\?&]gift_validate=1(?:&|$)/.test(window.location.search)) {
      return;
    }
    if (window.Shopify?.designMode) {
      this.debugLog('[GIFTS-PROGRESS] Skipping gift variant validation in theme editor');
      return;
    }

    const giftMilestones = this.milestones.filter(m => m.type === 'gift' && m.variantId);
    
    for (const milestone of giftMilestones) {
      try {
        const response = await this._postCartJson('cart/add.js', {
          id: parseInt(milestone.variantId),
          quantity: 0,
          properties: { '_validation': 'true' },
        });
        
        if (response.ok) {
          this.debugLog(`[GIFTS-PROGRESS] ✅ Gift variant validated: ${milestone.variantId}`);
        } else {
          if (response.status === 422) {
            this.debugError(`[GIFTS-PROGRESS] ❌ Gift variant not available: ${milestone.variantId}. Please check if the variant exists and is in stock.`);
          } else {
            this.debugError(`[GIFTS-PROGRESS] ❌ Failed to validate gift variant ${milestone.variantId}: ${response.status}`);
          }
        }
      } catch (error) {
        this.debugError(`[GIFTS-PROGRESS] ❌ Error validating gift variant ${milestone.variantId}:`, error);
      }
    }
  }

  async addGiftToCart(milestone, giftKey) {
    try {
      this.debugLog('[GIFTS-PROGRESS] Adding gift to cart:', giftKey);
      
      // Validate variant ID
      if (!milestone.variantId || milestone.variantId === '') {
        this.debugWarn('[GIFTS-PROGRESS] No variant ID provided for gift milestone:', giftKey);
        return;
      }

      const response = await this._postCartJson('cart/add.js', {
        id: parseInt(milestone.variantId),
        quantity: 1,
        properties: {
          '_gift': 'true',
          '_gift_threshold': milestone.threshold,
          '_gift_key': giftKey,
        },
      });

      if (response.ok) {
        const cart = await this._loadCartFromServer();

        // Find the gift line item in the updated cart
        const giftLineItem = cart.items.find(item => 
          item.variant_id === parseInt(milestone.variantId) && 
          item.properties && 
          item.properties._gift === 'true' &&
          item.properties._gift_key === giftKey
        );
        
        if (giftLineItem) {
          // Store the line item ID for future removal
          this.giftLineItems.set(giftKey, giftLineItem.key);
          this.addedGifts.set(giftKey, {
            variantId: milestone.variantId,
            threshold: milestone.threshold,
            lineItemKey: giftLineItem.key
          });
        }
        
        // Don't dispatch individual cart events - let the batch handler do it
        
        this.debugLog('[GIFTS-PROGRESS] Gift added to cart:', milestone.variantId);
      } else {
        this.debugWarn('[GIFTS-PROGRESS] Failed to add gift to cart:', await response.text());
      }
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to add gift to cart:', error);
    }
  }

  async adjustGiftQuantity(milestone, giftKey, cartItems, targetQuantity) {
    try {
      this.debugLog('[GIFTS-PROGRESS] Adjusting free gift quantity:', giftKey, 'to', targetQuantity);
      
      // Find all FREE gift line items for this milestone (with gift properties)
      const freeGiftLineItems = cartItems.filter(item => 
        item.variant_id === parseInt(milestone.variantId) && 
        item.properties && 
        item.properties._gift === 'true' &&
        item.properties._gift_key === giftKey
      );

      if (freeGiftLineItems.length === 0) {
        this.debugLog('[GIFTS-PROGRESS] No free gift line items found for adjustment');
        return;
      }

      // If we have multiple free gift line items, remove all but the first one
      if (freeGiftLineItems.length > 1) {
        for (let i = 1; i < freeGiftLineItems.length; i++) {
          const response = await this._postCartJson('cart/change.js', {
            id: freeGiftLineItems[i].key,
            quantity: 0,
          });

          if (!response.ok) {
            this.debugWarn('[GIFTS-PROGRESS] Failed to remove duplicate free gift line item:', await response.text());
          }
        }
      }

      // Adjust the quantity of the first (or only) free gift line item
      const firstFreeGiftItem = freeGiftLineItems[0];
      
      const response = await this._postCartJson('cart/change.js', {
        id: firstFreeGiftItem.key,
        quantity: targetQuantity,
      });

      if (response.ok) {
        await this._loadCartFromServer();

        // Don't dispatch individual cart events - let the batch handler do it

        this.debugLog('[GIFTS-PROGRESS] Free gift quantity adjusted to', targetQuantity);
        this.debugLog('[GIFTS-PROGRESS] Note: Any additional quantities of this product will be paid items');
      } else {
        this.debugWarn('[GIFTS-PROGRESS] Failed to adjust free gift quantity:', await response.text());
      }
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to adjust free gift quantity:', error);
    }
  }

  async removeGiftFromCart(milestone, giftKey, cartItems) {
    try {
      this.debugLog('[GIFTS-PROGRESS] Removing free gift from cart:', giftKey);
      
      const giftData = this.addedGifts.get(giftKey);
      if (!giftData) {
        this.debugLog('[GIFTS-PROGRESS] Gift data not found for removal:', giftKey);
        return;
      }

      const freshCart = await this._loadCartFromServer();
      
      // Find all FREE gift line items for this milestone (with gift properties) in fresh cart
      const freeGiftLineItems = freshCart.items.filter(item => 
        item.variant_id === parseInt(milestone.variantId) && 
        item.properties && 
        item.properties._gift === 'true' &&
        item.properties._gift_key === giftKey
      );

      if (freeGiftLineItems.length === 0) {
        this.debugLog('[GIFTS-PROGRESS] No free gift line items found in fresh cart');
        // Still remove from tracking even if not found in cart
        this.addedGifts.delete(giftKey);
        this.giftLineItems.delete(giftKey);
        return;
      }

      this.debugLog('[GIFTS-PROGRESS] Found', freeGiftLineItems.length, 'free gift line items to remove in fresh cart');

      let removalSuccess = true;
      
      // Remove all free gift line items using fresh cart data
      for (const giftLineItem of freeGiftLineItems) {
        this.debugLog('[GIFTS-PROGRESS] Removing gift', giftLineItem.key, 'for variant', giftLineItem.variant_id);
        
        const response = await this._postCartJson('cart/change.js', {
          id: giftLineItem.key,
          quantity: 0,
        });

        if (!response.ok) {
          const errorText = await response.text();
          this.debugWarn('[GIFTS-PROGRESS] Failed to remove free gift line item:', errorText);
          removalSuccess = false;
        } else {
          this.debugLog('[GIFTS-PROGRESS] Successfully removed gift', giftLineItem.key);
        }
      }

      if (removalSuccess) {
        const verifyCart = await this._loadCartFromServer();

        const remainingGifts = verifyCart.items.filter(item => 
          item.variant_id === parseInt(milestone.variantId) && 
          item.properties && 
          item.properties._gift === 'true' &&
          item.properties._gift_key === giftKey
        );
        
        if (remainingGifts.length === 0) {
          this.debugLog('[GIFTS-PROGRESS] ✅ Gift successfully removed from cart');
          this.addedGifts.delete(giftKey);
          this.giftLineItems.delete(giftKey);
        } else {
          this.debugWarn('[GIFTS-PROGRESS] ⚠️ Gift still present after removal attempt:', remainingGifts.length, 'items remain');
        }
      }
      
      this.debugLog('[GIFTS-PROGRESS] Free gift removal process completed');
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to remove free gift from cart:', error);
    }
  }

  updateProgress() {
    this.debugLog('[GIFTS-PROGRESS] Updating progress, cart total:', this.cartTotal);
    
    const progressElement = this.querySelector('.gifts-progress__fill');
    const mainMessageElement = this.querySelector('.gifts-progress__message');
    
    if (!progressElement) {
      this.debugLog('[GIFTS-PROGRESS] Missing required elements, skipping update');
      return;
    }

    // Filter out hidden milestones for visual display
    const visibleMilestones = this.milestones.filter(m => !m.hidden);
    const sortedMilestones = [...visibleMilestones].sort((a, b) => a.threshold - b.threshold);
    const maxThreshold = sortedMilestones[sortedMilestones.length - 1]?.threshold || 10000;
    const freeShipUnlock = this.hasFreeShipOverride();
    const shippingMilestone = sortedMilestones.find((m) => m.type === 'shipping');
    const progressTotal =
      freeShipUnlock && shippingMilestone
        ? Math.max(this.cartTotal, shippingMilestone.threshold)
        : this.cartTotal;
    
    // Update progress bar
    const progressPercentage = Math.min((progressTotal / maxThreshold) * 100, 100);
    progressElement.style.width = `${progressPercentage}%`;

    // Position milestones proportionally along the track
    sortedMilestones.forEach((milestone, index) => {
      const milestonePercentage = (milestone.threshold / maxThreshold) * 100;
      
      // Handle edge cases to prevent overflow
      let leftPosition = milestonePercentage;
      if (milestonePercentage >= 100) {
        leftPosition = 100;
        milestone.element.style.setProperty('transform', 'translateX(-100%)', 'important');
      } else if (milestonePercentage <= 0) {
        leftPosition = 0;
        milestone.element.style.setProperty('transform', 'translateX(0%)', 'important');
      } else {
        milestone.element.style.setProperty('transform', 'translateX(-50%)', 'important');
      }
      
      // Force the positioning with !important to override any CSS
      milestone.element.style.setProperty('left', `${leftPosition}%`, 'important');
      milestone.element.style.setProperty('position', 'absolute', 'important');
      
      const isAchieved =
        this.cartTotal >= milestone.threshold ||
        (freeShipUnlock && milestone.type === 'shipping');
      milestone.element.classList.toggle('gifts-progress__milestone--achieved', isAchieved);
      milestone.element.classList.toggle(
        'gifts-progress__milestone--free-ship-unlocked',
        freeShipUnlock && milestone.type === 'shipping'
      );
      
      this.debugLog(`[GIFTS-PROGRESS] Milestone ${milestone.threshold} (${index}): positioned at ${leftPosition}%, achieved: ${isAchieved}`);
    });

    // Find current status
    const isMilestoneAchieved = (m) =>
      this.cartTotal >= m.threshold || (freeShipUnlock && m.type === 'shipping');
    const achievedMilestones = sortedMilestones.filter(isMilestoneAchieved);
    const nextMilestone = sortedMilestones.find((m) => !isMilestoneAchieved(m));

    // Update messages
    if (mainMessageElement && shippingMilestone && !freeShipUnlock && this.cartTotal > 0 && this.cartTotal < shippingMilestone.threshold) {
      const remainingAmount = shippingMilestone.threshold - this.cartTotal;
      mainMessageElement.innerHTML = `Spend <strong>${this.formatMoney(remainingAmount)}</strong> more to earn <strong>free shipping</strong>!`;
    } else if (mainMessageElement && achievedMilestones.length > 0) {
      const latestAchieved = achievedMilestones[achievedMilestones.length - 1];
      if (latestAchieved.type === 'shipping') {
        mainMessageElement.innerHTML = 'You unlocked <strong>free shipping</strong>!';
      } else {
        mainMessageElement.innerHTML = '<strong>Free gift</strong> unlocked!';
      }
    } else if (mainMessageElement) {
      mainMessageElement.innerHTML = 'Unlock free shipping!';
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: Shopify.currency?.active || 'USD'
    }).format(cents / 100);
  }

  dispatchCartChangeEvent(cart, baseEvent) {
    try {
      if (this.cartEventDebounceTimer) {
        clearTimeout(this.cartEventDebounceTimer);
      }

      this.cartEventDebounceTimer = setTimeout(() => {
        this.cartEventDebounceTimer = null;
        const pending = this.pendingCartUpdates.size;
        const totalChanged = cart.total_price !== this.lastCartTotal;
        const countChanged = cart.item_count !== this.lastItemCount;

        if (totalChanged || countChanged || pending > 0) {
          if (pending > 0) {
            this.pendingCartUpdates.clear();
          }
          void this.notifyThemeCartUpdated(cart, baseEvent);
          this.debugLog('[GIFTS-PROGRESS] Dispatched debounced cart change event');
        } else {
          this.debugLog('[GIFTS-PROGRESS] Skipped cart event (no meaningful change)');
        }
      }, 100);
    } catch (error) {
      this.debugWarn('[GIFTS-PROGRESS] Failed to dispatch cart change event:', error);
    }
  }
}

// Register the custom element
window.customElements.define('gifts-progress', GiftsProgress);

} // End of redeclaration check
